import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem
} from "@mui/material";

import {
  payExpense,
  createPayment
} from "../../api/accountingApi";

/*
===========================================================
UNIFIED PAYMENT MODAL (ERP LEVEL DESIGN)
-----------------------------------------------------------
Used for:
- Expense Payment
- Vendor Payment
- Invoice Receipt
- General Payment Entry

Props:
- open         → modal visibility
- onClose      → close modal
- onSuccess    → callback to refresh parent data
- type         → expense | vendor | invoice | general
- referenceId  → id of related record
- defaultAmount→ pre-filled amount
===========================================================
*/

export default function PaymentModal({
  open,
  onClose,
  onSuccess,        // 🔥 IMPORTANT (for refreshing UI)
  type,
  referenceId,
  defaultAmount = 0
}) {

  /*
  ===========================================================
  STATE
  ===========================================================
  */
  const [form, setForm] = useState({
    amount: defaultAmount,
    currency: "INR",
    exchange_rate: 1,
    bank_id: "",
    notes: ""
  });

  /*
  ===========================================================
  RESET FORM WHEN MODAL OPENS
  (VERY IMPORTANT - avoids stale data)
  ===========================================================
  */
  useEffect(() => {
    if (open) {
      setForm({
        amount: defaultAmount || 0,
        currency: "INR",
        exchange_rate: 1,
        bank_id: "",
        notes: ""
      });
    }
  }, [open, defaultAmount]);

  /*
  ===========================================================
  HANDLE INPUT CHANGE
  ===========================================================
  */
  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  /*
  ===========================================================
  HANDLE SUBMIT
  ===========================================================
  */
  const handleSubmit = async () => {
    try {

      /*
      =========================
      EXPENSE PAYMENT
      =========================
      */
      if (type === "expense") {
        await payExpense({
          expense_id: referenceId,
          amount: Number(form.amount),
          bank_id: form.bank_id
        });
      }

      /*
      =========================
      GENERAL / INVOICE / VENDOR
      =========================
      */
      else {
        await createPayment({
          reference_id: referenceId,
          type,
          amount: Number(form.amount),
          currency: form.currency,
          exchange_rate: Number(form.exchange_rate),
          bank_id: form.bank_id,
          notes: form.notes
        });
      }

      /*
      =========================
      SUCCESS HANDLING
      =========================
      */
      alert("Payment successful ✅");

      // 🔥 CRITICAL: REFRESH PARENT PAGE
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();

    } catch (err) {
      console.error("Payment Error:", err);
      alert("Payment failed ❌");
    }
  };

  /*
  ===========================================================
  TITLE BASED ON TYPE
  ===========================================================
  */
  const getTitle = () => {
    switch (type) {
      case "expense":
        return "Pay Expense";
      case "vendor":
        return "Vendor Payment";
      case "invoice":
        return "Receive Payment";
      default:
        return "Record Payment";
    }
  };

  /*
  ===========================================================
  UI
  ===========================================================
  */
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">

      {/* ================= TITLE ================= */}
      <DialogTitle>{getTitle()}</DialogTitle>

      {/* ================= CONTENT ================= */}
      <DialogContent>

        <Grid container spacing={2} mt={1}>

          {/* AMOUNT */}
          <Grid item xs={6}>
            <TextField
              label="Amount"
              name="amount"
              type="number"
              fullWidth
              value={form.amount}
              onChange={handleChange}
            />
          </Grid>

          {/* CURRENCY */}
          <Grid item xs={6}>
            <TextField
              label="Currency"
              name="currency"
              fullWidth
              value={form.currency}
              onChange={handleChange}
            />
          </Grid>

          {/* EXCHANGE RATE */}
          <Grid item xs={6}>
            <TextField
              label="Exchange Rate"
              name="exchange_rate"
              type="number"
              fullWidth
              value={form.exchange_rate}
              onChange={handleChange}
            />
          </Grid>

          {/* BANK */}
          <Grid item xs={6}>
            <TextField
              label="Bank ID"
              name="bank_id"
              fullWidth
              value={form.bank_id}
              onChange={handleChange}
            />
          </Grid>

          {/* NOTES */}
          <Grid item xs={12}>
            <TextField
              label="Notes"
              name="notes"
              fullWidth
              multiline
              rows={2}
              value={form.notes}
              onChange={handleChange}
            />
          </Grid>

        </Grid>

      </DialogContent>

      {/* ================= ACTIONS ================= */}
      <DialogActions>

        <Button onClick={onClose}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
        >
          Submit
        </Button>

      </DialogActions>

    </Dialog>
  );
}